import React from 'react'
import { useIntl, FormatDateOptions } from 'react-intl'
import { CustomLinkComponent } from 'msp-components'
import {
  standingOrderCalculation,
  type StandingOrderItemType,
  type StandingOrderType
} from 'msp-integrations'
import useProduct from '@/hooks/use-product'
import { MAX_IMAGES_TO_DISPLAY } from '@/constants'
import StandingOrdersItemImage from './StandingOrdersItemImage'
import { AccountStandingOrdersStatus } from '../AccountStandingOrdersStatus'
import { translations } from '../AccountStandingOrders.constants'
import * as S from '../AccountStandingOrders.styles'

export const StandingOrdersItem = ({ order }: StandingOrderType) => {
  const { formatMessage, formatNumber, formatDate } = useIntl()
  const dateFormattingOpts = {
    year: 'numeric',
    day: '2-digit',
    month: '2-digit'
  } as FormatDateOptions
  const productListItems = order?.customerProductListItems
  const productQuantity = productListItems?.length || 0
  const hasTooManyProducts = () => productQuantity > MAX_IMAGES_TO_DISPLAY

  const products = productListItems?.map((p: StandingOrderItemType) => ({
    ...useProduct(p.productId),
    quantity: p.quantity
  }))
  const pricing = standingOrderCalculation(
    products,
    order?.c_standingOrderCoupon ? undefined : order?.c_orderingShipping
  )

  return (
    <CustomLinkComponent
      colorScheme='primary'
      href={`/account/subscriptions/${order.id}`}
      uppercase={false}
      inline
    >
      <div className={S.ItemWrapper}>
        <div className={S.ItemInfo}>
          <div className={S.ItemColLeft}>
            <div className={S.ItemTitleLink}>
              <AccountStandingOrdersStatus
                status={order?.c_orderingStatus}
                className='mr-5'
              />
              <span className='leading-20 text-10'>#{order.id}</span>
            </div>
            <ul className={S.ItemTableWrapper}>
              <li className={S.ItemTableRow}>
                <span className={S.ItemTableCellLabel}>
                  {formatMessage(translations.orderStartDateLabel)}
                </span>
                <span className={S.ItemTableCellValue}>
                  {formatMessage(translations.orderStartDate, {
                    startDate: formatDate(
                      order.c_orderingStartDate,
                      dateFormattingOpts
                    )
                  })}
                </span>
              </li>
              <li className={S.ItemTableRow}>
                <span className={S.ItemTableCellLabel}>
                  {formatMessage(translations.orderDeliveryIntervalLabel)}
                </span>
                <span className={S.ItemTableCellValue}>
                  {formatMessage(translations.orderDeliveryInterval, {
                    weeks: order.c_orderingInterval
                  })}
                </span>
              </li>
              <li className={S.ItemTableRow}>
                <span className={S.ItemTableCellLabel}>
                  {formatMessage(translations.orderNextShippingDateLabel)}
                </span>
                <span className={S.ItemTableCellValue}>
                  {formatDate(
                    order.c_orderingNextShippingDate,
                    dateFormattingOpts
                  )}
                </span>
              </li>
              <li className={S.ItemTableRow}>
                <span className={S.ItemTableCellLabel}>
                  {formatMessage(translations.orderTotalPrice)}
                </span>
                <span className={S.ItemTableCellValue}>
                  {formatNumber(
                    pricing.discountedTotal > 0 &&
                      pricing.discountedTotal !== pricing.shipping
                      ? pricing.discountedTotal
                      : pricing.total,
                    {
                      style: 'currency',
                      currency: pricing?.currency,
                      currencyDisplay: 'code'
                    }
                  )}
                </span>
              </li>
            </ul>
          </div>
          <div className={S.ItemColRight}>
            {products?.length > 0 &&
              products
                ?.slice(0, MAX_IMAGES_TO_DISPLAY)
                .map((product: StandingOrderItemType, ind: number) => (
                  <StandingOrdersItemImage
                    key={`${order.id}-${product.id}-${ind}`}
                    image={product?.c_image}
                    altText={product?.name}
                    productQty={productQuantity}
                    showOverlay={
                      hasTooManyProducts() && ind + 1 === MAX_IMAGES_TO_DISPLAY
                    }
                  />
                ))}
          </div>
        </div>
        <div className={S.ActionsWrapper}>
          {formatMessage(translations.orderViewDetail)}
        </div>
      </div>
    </CustomLinkComponent>
  )
}
